import { Suspense } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, Shield } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Logo } from '@/components/Logo';
import { ConviteDeInstalacao, FaixaOffline } from '@/components/PwaBits';
import { PageLoader } from '@/components/ui';
import { useAuth, useIsAdmin, useUser } from '@/lib/auth';
import { initials } from '@/lib/format';
import { PRIMARY_NAV, isNavActive, pageTitle } from './nav';

/**
 * The authenticated shell.
 *
 * Mobile: a 56px fixed top bar and a fixed bottom nav whose five destinations
 * carry both an icon and a label. From `lg` up the bottom nav becomes a 240px
 * sidebar and the top bar slims to 48px. The content column reserves room for
 * both bars and respects the iOS safe-area insets on all four edges.
 */
export function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useUser();
  const isAdmin = useIsAdmin();
  const logout = useAuth((state) => state.logout);
  const title = pageTitle(location.pathname);

  const signOut = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-dvh bg-bg">
      <a
        href="#conteudo"
        className={cn(
          'sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50',
          'focus:inline-flex focus:min-h-11 focus:items-center focus:rounded-md focus:border focus:border-border-strong',
          'focus:bg-surface-2 focus:px-4 focus:text-base',
        )}
      >
        Pular para o conteúdo
      </a>

      {/* --- Sidebar (lg and up) --------------------------------------- */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 hidden w-[var(--width-sidebar)] flex-col',
          'border-r border-border bg-surface pt-[env(safe-area-inset-top)] lg:flex',
        )}
      >
        <div className="flex h-14 shrink-0 items-center px-4">
          <Link
            to="/"
            className="flex min-h-11 items-center rounded-md"
            aria-label="MychronoHelper — início"
          >
            <Logo size="md" />
          </Link>
        </div>

        <nav aria-label="Navegação principal" className="flex-1 overflow-y-auto px-2 py-2">
          <ul className="flex flex-col gap-1">
            {PRIMARY_NAV.map((item) => {
              const active = isNavActive(item, location.pathname);
              const Icon = item.icon;
              return (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'relative flex min-h-11 items-center gap-3 rounded-md px-3 py-2 text-base font-medium',
                      'transition-colors duration-150 ease-out-soft',
                      active
                        ? 'bg-primary-dim text-text'
                        : 'text-text-muted hover:bg-surface-2 hover:text-text',
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        'absolute inset-y-1.5 left-0 w-0.5 rounded-full',
                        active ? 'bg-primary' : 'bg-transparent',
                      )}
                    />
                    <Icon aria-hidden="true" className="size-5 shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="shrink-0 border-t border-border p-2">
          {isAdmin ? (
            <Link
              to="/admin"
              className={cn(
                'mb-1 flex min-h-11 items-center gap-3 rounded-md px-3 py-2 text-base font-medium',
                'text-text-muted transition-colors duration-150 ease-out-soft hover:bg-surface-2 hover:text-text',
              )}
            >
              <Shield aria-hidden="true" className="size-5 shrink-0" />
              <span>Administração</span>
            </Link>
          ) : null}

          <div className="flex items-center gap-3 rounded-md px-3 py-2">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-surface-3 text-xs font-bold text-text-muted">
              {initials(user?.name ?? user?.nickname)}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold text-text">
                {user?.nickname ?? '—'}
              </span>
              <span className="block truncate text-2xs text-text-faint">{user?.email}</span>
            </span>
            <button
              type="button"
              onClick={() => void signOut()}
              aria-label="Sair da conta"
              title="Sair da conta"
              className={cn(
                'flex size-11 shrink-0 items-center justify-center rounded-md text-text-muted',
                'transition-colors duration-150 ease-out-soft hover:bg-surface-2 hover:text-loss',
              )}
            >
              <LogOut aria-hidden="true" className="size-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* --- Top bar ---------------------------------------------------- */}
      <header
        className={cn(
          'fixed inset-x-0 top-0 z-30 border-b border-border bg-surface/95 backdrop-blur',
          'pt-[env(safe-area-inset-top)] lg:left-[var(--width-sidebar)]',
        )}
      >
        <div className="flex h-[var(--height-header)] items-center gap-3 px-gutter lg:h-12">
          <Link
            to="/"
            className="flex size-11 shrink-0 items-center justify-center rounded-md lg:hidden"
            aria-label="MychronoHelper — início"
          >
            <Logo variant="mark" size="sm" />
          </Link>

          <h1 className="min-w-0 flex-1 truncate text-lg text-text lg:text-xl">{title}</h1>

          {isAdmin ? (
            <Link
              to="/admin"
              aria-label="Painel administrativo"
              title="Painel administrativo"
              className={cn(
                'flex size-11 items-center justify-center rounded-md text-text-muted lg:hidden',
                'transition-colors duration-150 ease-out-soft hover:bg-surface-2 hover:text-text',
              )}
            >
              <Shield aria-hidden="true" className="size-5" />
            </Link>
          ) : null}

          <Link
            to="/perfil"
            aria-label={`Perfil de ${user?.nickname ?? 'piloto'}`}
            className={cn(
              'flex size-11 items-center justify-center rounded-full lg:hidden',
              'transition-colors duration-150 ease-out-soft hover:bg-surface-2',
            )}
          >
            <span className="flex size-8 items-center justify-center rounded-full bg-surface-3 text-xs font-bold text-text-muted">
              {initials(user?.name ?? user?.nickname)}
            </span>
          </Link>
        </div>
      </header>

      {/* --- Content ---------------------------------------------------- */}
      <div className="lg:pl-[var(--width-sidebar)]">
        <FaixaOffline />
        <main
          id="conteudo"
          className={cn(
            'safe-bottom mx-auto w-full max-w-[1400px] px-gutter',
            'pt-[calc(var(--height-header)+env(safe-area-inset-top)+1rem)]',
            'lg:pt-[calc(3rem+env(safe-area-inset-top)+1.5rem)]',
          )}
        >
          <Suspense fallback={<PageLoader />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      <ConviteDeInstalacao />

      {/* --- Bottom nav (below lg) -------------------------------------- */}
      <nav
        aria-label="Navegação principal"
        className={cn(
          'fixed inset-x-0 bottom-0 z-40 border-t border-border bg-surface/95 backdrop-blur',
          'pb-[env(safe-area-inset-bottom)] lg:hidden',
        )}
      >
        <ul className="flex h-[var(--height-bottom-nav)] items-stretch">
          {PRIMARY_NAV.map((item) => {
            const active = isNavActive(item, location.pathname);
            const Icon = item.icon;
            return (
              <li key={item.to} className="flex-1">
                <NavLink
                  to={item.to}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'relative flex h-full min-h-11 flex-col items-center justify-center gap-0.5 px-1',
                    'transition-colors duration-150 ease-out-soft',
                    active ? 'text-primary' : 'text-text-faint hover:text-text-muted',
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      'absolute inset-x-3 top-0 h-0.5 rounded-b-full',
                      active ? 'bg-primary' : 'bg-transparent',
                    )}
                  />
                  <Icon aria-hidden="true" className="size-5 shrink-0" strokeWidth={active ? 2.5 : 2} />
                  <span className={cn('text-2xs leading-none', active && 'font-semibold')}>{item.label}</span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}

export default AppLayout;
